import React, { useState, useRef } from 'react'
import {
  AiOutlineFullscreen,
  AiOutlineFullscreenExit,
  AiOutlineZoomIn,
  AiOutlineZoomOut,
} from 'react-icons/ai'

const ZoomableImage = ({ src, alt }) => {
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [isDragging, setIsDragging] = useState(false) // Track drag state
  const [position, setPosition] = useState({ x: 0, y: 0 }) // Track position of image while dragging
  const imageRef = useRef(null)
  const containerRef = useRef(null)

  const handleOpenModal = () => setIsZoomed(true)
  const handleCloseModal = () => {
    setIsZoomed(false)
    setZoomLevel(1) // Reset zoom level
    setPosition({ x: 0, y: 0 }) // Reset position
  }

  const handleZoomIn = () =>
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.2, 3)) // Cap zoom at 3x
  const handleZoomOut = () =>
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.2, 1)) // Minimum zoom is 1x

  // Handle mouse down event for starting drag
  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true)
      const rect = containerRef.current.getBoundingClientRect()
      setPosition({
        x: e.clientX - rect.left - imageRef.current.offsetLeft,
        y: e.clientY - rect.top - imageRef.current.offsetTop,
      })
    }
  }

  // Handle mouse move event for dragging
  const handleMouseMove = (e) => {
    if (isDragging) {
      const rect = containerRef.current.getBoundingClientRect()
      const newX = e.clientX - rect.left - position.x
      const newY = e.clientY - rect.top - position.y
      imageRef.current.style.transform = `scale(${zoomLevel}) translate(${newX}px, ${newY}px)`
    }
  }

  // Handle mouse up event to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false)
  }

  return (
    <>
      {/* Image container with button */}
      <div className="relative overflow-hidden w-full my-20">
        <img src={src} alt={alt} className="object-cover w-full h-full" />

        {/* Zoom Button */}
        <button
          onClick={handleOpenModal}
          className=" absolute -top-10 lg:top-2 right-2 bg-black bg-opacity-70 p-2 rounded-full"
        >
          <AiOutlineFullscreen className="text-white" size={24} />
        </button>
      </div>

      {/* Modal for full screen image with zoom controls */}
      {isZoomed && (
        <div
          className="fixed inset-0 md:p-10 bg-black bg-opacity-90 flex items-center justify-center z-50"
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <div
            ref={containerRef}
            className="w-full h-full flex justify-center items-center relative overflow-hidden"
            onMouseDown={handleMouseDown}
          >
            {/* Zoomable and draggable Image */}
            <img
              ref={imageRef}
              src={src}
              alt={alt}
              className=""
              style={{
                transform: `scale(${zoomLevel}) translate(0px, 0px)`,
                cursor: zoomLevel > 1 ? 'grab' : 'default',
              }}
            />
          </div>

          {/* Zoom Controls */}
          <div className="absolute bottom-4  flex justify-center space-x-2">
            <button
              onClick={handleZoomIn}
              className="bg-white bg-opacity-80 hover:bg-opacity-70 outline-2 outline outline-white p-2 rounded-full"
            >
              <AiOutlineZoomIn className="text-black" size={24} />
            </button>
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="bg-white bg-opacity-80 hover:bg-opacity-70 outline-2 outline outline-white p-2 rounded-full"
            >
              <AiOutlineFullscreenExit className="text-black" size={24} />
            </button>
            <button
              onClick={handleZoomOut}
              className="bg-white bg-opacity-80 hover:bg-opacity-70 outline-2 outline outline-white p-2 rounded-full"
            >
              <AiOutlineZoomOut className="text-black" size={24} />
            </button>
          </div>
        </div>
      )}
    </>
  )
}

export default ZoomableImage
